import React, { useMemo, useState } from 'react';
import { View, Text, Dimensions, StyleSheet } from 'react-native';
import { LineChart } from 'react-native-chart-kit';
import Svg, { Circle, G, Text as SvgText, Line, Defs, LinearGradient, Stop, Path } from 'react-native-svg';
import { ScaledSheet } from '@utils/scale';

type IncomePoint = { date: string; amount: number, status: string };

type Props = {
  title?: string;
  monthLabel?: string;               
  modeLabel?: string;                
  today?: Date | null;                      
  incomes: IncomePoint[];            
  payout?: {                         
    date: string;                    
    amount: number;                  
    label?: string;                  
  } | null;
  dayInitials?: string[];
};

const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
const dateKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const rupee = (n: number) => `₹${n}`;

export default function LineChartCard({
  title,
  monthLabel,
  modeLabel = 'Weekly',
  today,
  incomes,
  payout = null,
  dayInitials = [],
}: Props) {

  // Build days array directly from incomes
  const days = useMemo(() => {
    return incomes.map((income) => {
      const d = new Date(income.date);
      d.setHours(0, 0, 0, 0);
      return d;
    });
  }, [incomes]);

  // Use provided monthLabel or derive from first income date
  const month = useMemo(() => {
    if (monthLabel) return monthLabel;
    if (incomes.length > 0) {
      return new Intl.DateTimeFormat('en', { month: 'long' }).format(new Date(incomes[0].date));
    }
    return '';
  }, [monthLabel, incomes]);

  // Map incomes to date keys for efficient lookup
  const incomeMap = useMemo(() => {
    const m = new Map<string, number>();
    incomes.forEach(p => m.set(p.date, p.amount));
    return m;
  }, [incomes]);

  // Empty labels for chart (we draw custom labels)
  const labels = days.map(() => '');
  
  // Data array from incomes (already aligned since days are built from incomes)
  const data = incomes.map(income => income.amount);
  
  // Convert to chart-compatible format
  const chartData = data;

  // Find min/max for scaling, including payout amount
  const allValues = payout ? [...data, payout.amount] : data;
  const min = Math.min(...allValues);
  const max = Math.max(...allValues);
  
  // Find today's index if today is provided
  const todayIdx = useMemo(() => {
    if (!today) return -1;
    const todayKey = dateKey(new Date(today));
    return days.findIndex(d => dateKey(d) === todayKey);
  }, [today, days]);
  
  // Find payout index if payout is provided
  const payoutIdx = useMemo(() => {
    if (!payout) return -1;
    return days.findIndex(d => dateKey(d) === payout.date);
  }, [payout, days]);

  const [chartWidth, setChartWidth] = useState(Dimensions.get('window').width - 32);
  const chartHeight = 100;

  return (
    <View 
      style={styles.card}
      onLayout={(event) => {
        const { width } = event.nativeEvent.layout;
        setChartWidth(width);
      }}
    >
      {/* Header */}
      <View style={styles.headerRow}>
        <View>
          <Text style={styles.title}>{title}</Text>
          <Text style={styles.subTitle}>{month}</Text>
        </View>
        <View style={styles.chip}>
          <Text style={styles.chipText}>{modeLabel}</Text>
        </View>
      </View>

      {/* Line Chart */}
      <LineChart
        data={{
          labels,
          datasets: [{ data: chartData }],
        }}
        width={chartWidth}
        height={chartHeight}
        withHorizontalLabels={false}
        withVerticalLabels={false}
        withInnerLines={false}
        withOuterLines={false}
        withShadow={false}
        withVerticalLines={false}
        withHorizontalLines={false}
        yAxisSuffix=""
        yAxisInterval={1}
        fromZero={false}
        chartConfig={{
          backgroundGradientTo: "#FFFFFF",
          backgroundGradientFromOpacity: 0,
          backgroundGradientFrom: "#FFFFFF",
          backgroundGradientToOpacity: 0,
          color: () => 'rgba(0,0,0,0)',        // hide library's line, we draw our own
          labelColor: () => '#000',
          fillShadowGradient: '#D3D3D3',       // grayish color for shadow
          fillShadowGradientFrom: '#D3D3D3',   // gray at top
          fillShadowGradientTo: '#FFFFFF',     // white at bottom
          fillShadowGradientOpacity: 0.4,      // opacity for gray
          fillShadowGradientToOpacity: 0,      // transparent white at bottom
          propsForDots: { r: '0' }, // hide default dots, we'll draw custom ones
          propsForBackgroundLines: { strokeWidth: 0 },
          strokeWidth: 1, // hairline width for the line chart
        }}
        bezier={false} // straight segments like your design
        transparent={true}
        segments={1}
        decorator={({ width, height }: { width: number; height: number }) => {
          // Match chart-kit's internal padding for line positioning
          // The library adds padding to prevent dots from being cut off
          const paddingLeft = 25;  // Increased padding for better spacing
          const paddingRight = 25; // Increased padding for better spacing
          const chartInnerWidth = width - paddingLeft - paddingRight;
          const step = chartInnerWidth / (data.length - 1);
          
          const yFor = (v: number) => {
            // reproduce chart-kit's internal scaling (approx)
            const range = (max - min) || 1;
            const topPad = 24;  // some padding inside the chart
            const bottomPad = 35;  // increased to accommodate payout label and amount
            const usable = height - topPad - bottomPad;
            return topPad + (max - v) * (usable / range);
          };

          return (
            <Svg width={width} height={height}>
              <Defs>
                <LinearGradient id="shadowGradient" x1="0" y1="0" x2="0" y2="1">
                  <Stop offset="0%" stopColor="#A0A0A0" stopOpacity="0.5" />
                  <Stop offset="50%" stopColor="#C8C8C8" stopOpacity="0.3" />
                  <Stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
                </LinearGradient>
              </Defs>
              <G>
                {/* Draw filled area under the line with gradient */}
                {(() => {
                  // Build path for the filled area (only up to today)
                  let pathData = '';
                  const validPoints: Array<{ x: number; y: number; index: number }> = [];
                  
                  data.forEach((v, i) => {
                    // Stop plotting after today
                    if (todayIdx !== -1 && i > todayIdx) return;
                    if (v !== null && v !== undefined) {
                      const x = paddingLeft + (i * step);
                      const y = yFor(v);
                      validPoints.push({ x, y, index: i });
                    }
                  });

                  if (validPoints.length === 0) return null;

                  // Start from bottom-left of first point
                  pathData = `M ${validPoints[0].x} ${height}`;
                  // Line up to first point
                  pathData += ` L ${validPoints[0].x} ${validPoints[0].y}`;
                  
                  // Draw line through all points
                  validPoints.forEach((point, idx) => {
                    if (idx > 0) {
                      pathData += ` L ${point.x} ${point.y}`;
                    }
                  });
                  
                  // Line down to bottom-right of last point
                  const lastPoint = validPoints[validPoints.length - 1];
                  pathData += ` L ${lastPoint.x} ${height}`;
                  // Close path back to start
                  pathData += ` Z`;

                  return (
                    <Path
                      key="shadow-area"
                      d={pathData}
                      fill="url(#shadowGradient)"
                    />
                  );
                })()}

                {/* Vertical grid lines */}
                {Array.from({ length: 7 }).map((_, i) => {
                  const x = paddingLeft + (i * step);
                  return (
                    <Line
                      key={`vline-${i}`}
                      x1={x}
                      y1={0}
                      x2={x}
                      y2={height}
                      stroke="#E5E5E5"
                      strokeWidth="1"
                    />
                  );
                })}

                {/* Draw connecting lines between dots */}
                {data.map((v, i) => {
                  // Stop plotting lines after today
                  if (todayIdx !== -1 && i > todayIdx) return null;
                  if (i === 0 || v === null || v === undefined) return null;
                  const prevValue = data[i - 1];
                  if (prevValue === null || prevValue === undefined) return null;
                  
                  const x1 = paddingLeft + ((i - 1) * step);
                  const y1 = yFor(prevValue);
                  const x2 = paddingLeft + (i * step);
                  const y2 = yFor(v);

                  return (
                    <Line
                      key={`line-${i}`}
                      x1={x1}
                      y1={y1}
                      x2={x2}
                      y2={y2}
                      stroke="#000"
                      strokeWidth="1"
                    />
                  );
                })}

                {/* Draw dots at each data point aligned with vertical lines */}
                {data.map((v, i) => {
                  if (v === null || v === undefined) return null;
                  const cx = paddingLeft + (i * step);
                  const cy = yFor(v);
                  const isAfterToday = todayIdx !== -1 && i > todayIdx;
                  const isProcessing = incomes[i]?.status === 'processing';

                  return (
                    <G key={`dot-group-${i}`}>
                      {/* Regular filled black dot */}
                      <Circle
                        cx={cx}
                        cy={cy}
                        r={4}
                        fill="#000"
                        stroke="#000"
                        strokeWidth={2}
                      />
                      {/* Outer circle for processing status */}
                      {isProcessing && (
                        <Circle
                          cx={cx}
                          cy={cy}
                          r={8}
                          fill="none"
                          stroke="#000"
                          strokeWidth={1.5}
                        />
                      )}
                      {/* Amount label above dot - hide after today */}
                      {!isAfterToday && (
                        <SvgText
                          x={cx}
                          y={cy - 14}
                          fontSize="9"
                          fontWeight="600"
                          textAnchor="middle"
                          fill="#000"
                        >
                          {rupee(v)}
                        </SvgText>
                      )}
                    </G>
                  );
                })}

                {/* Payout marker - hollow dot at payout amount value */}
                {payout && payoutIdx >= 0 && (() => {
                  const cx = paddingLeft + (payoutIdx * step);
                  const cy = yFor(payout.amount);  // Use payout amount, not data value

                  return (
                    <G key="payout">
                      <Circle 
                        cx={cx} 
                        cy={cy} 
                        r="4" 
                        stroke="#000" 
                        strokeWidth="1.5" 
                        fill="#fff" 
                      />
                      {/* Payout amount below dot */}
                      <SvgText
                        x={cx}
                        y={cy + 12}
                        fontSize="8"
                        fontWeight="600"
                        textAnchor="middle"
                        fill="#000"
                      >
                        {rupee(payout.amount)}
                      </SvgText>
                      {/* Payout label below amount */}
                      <SvgText
                        x={cx}
                        y={cy + 22}
                        fontSize="7"
                        fontWeight="500"
                        textAnchor="middle"
                        fill="#666"
                      >
                        {payout.label || 'W.Payout'}
                      </SvgText>
                    </G>
                  );
                })()}
              </G>
            </Svg>
          );
        }}
        style={{ alignSelf: 'center', marginTop: 8 }}
      />

      {/* Day pills row - aligned with chart */}
      <View style={[styles.daysRow, { width: chartWidth, alignSelf: 'center' }]}>
        {days.map((d, i) => {
          const isToday = i === todayIdx;
          const pillStyles = [
            styles.pill,
            isToday && styles.pillToday,
          ];

          return (
            <View key={i} style={styles.pillWrap}>
              <View style={pillStyles}>
                <Text style={[styles.pillTop]}>
                  {dayInitials[d.getDay()]}
                </Text>
                <Text style={[styles.pillBottom]}>
                  {d.getDate()}
                </Text>
              </View>
            </View>
          );
        })}
      </View>
    </View>
  );
}

const styles = ScaledSheet.create({
  card: {
    backgroundColor: '#fff',
    borderRadius: 18,
    padding: 16,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: { fontSize: 20, fontWeight: '800', color: '#000' },
  subTitle: { marginTop: 2, fontSize: 14, color: '#777' },
  chip: {
    backgroundColor: '#F2F2F2',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  chipText: { fontSize: 13, fontWeight: '600', color: '#555' },

  daysRow: {
    marginTop: 6,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },
  pillWrap: { flex: 1, alignItems: 'center' },
  pill: {
    width: 44,
    height: 64,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: 'lightgray',
  },
  pillFaded: {
    opacity: 0.45,
  },
  pillTop: { fontSize: 13, fontWeight: '700', color: '#777' },
  pillBottom: { fontSize: 18, fontWeight: '800', color: '#000' },
  dim: { color: '#A9A9A9' },
  pillToday: {
    backgroundColor: '#F3F3F3',
    borderWidth: 0,
  },
});
